import React from 'react';
import { useProgressiveImage } from '@/hooks/useProgressiveImage';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  placeholder?: string;
  fallback?: string;
  loading?: 'lazy' | 'eager';
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  placeholder,
  fallback = '/placeholder.svg',
  loading = 'lazy',
  className,
  alt = '',
  ...props
}) => {
  const { src: currentSrc, loading: isLoading, error } = useProgressiveImage(src, placeholder);

  if (error && fallback) {
    return (
      <img
        src={fallback}
        alt={alt}
        className={cn('transition-opacity duration-300', className)}
        loading={loading}
        {...props}
      />
    );
  }

  return (
    <div className="relative">
      <img
        src={currentSrc}
        alt={alt}
        className={cn(
          'transition-opacity duration-300',
          isLoading ? 'opacity-0' : 'opacity-100',
          className
        )}
        loading={loading}
        {...props}
      />
      {isLoading && placeholder && (
        <div className="absolute inset-0 bg-muted animate-pulse rounded" />
      )}
    </div>
  );
};

export default OptimizedImage;